import React, { useState, useEffect } from 'react';
import { useRAGStore } from '../store/ragStore';
import { Shield, Plus, Trash2, RefreshCw } from 'lucide-react';

const ProtectedKeywordsTab: React.FC = () => {
  const {
    currentContext,
    uploadProtectedKeywords,
    refreshCurrentContext,
  } = useRAGStore();

  const [keywords, setKeywords] = useState<string[]>([]);
  const [newKeyword, setNewKeyword] = useState('');

  useEffect(() => {
    if (currentContext?.protectedKeywords) {
      setKeywords([...currentContext.protectedKeywords]);
    } else {
      setKeywords([]);
    }
  }, [currentContext]);

  const addKeyword = async () => {
    const keyword = newKeyword.trim();
    if (!keyword) {
      alert('Please enter a keyword');
      return;
    }

    if (keywords.includes(keyword)) {
      alert('Keyword already exists');
      return;
    }

    const newKeywords = [...keywords, keyword];
    setKeywords(newKeywords);
    setNewKeyword('');
    
    // Auto-save after adding keyword
    try {
      await uploadProtectedKeywords({ protected: newKeywords });
    } catch (error) {
      alert('Failed to save protected keywords');
      // Revert the change if save failed
      setKeywords([...keywords]);
    }
  };

  const removeKeyword = async (index: number) => {
    const newKeywords = keywords.filter((_, i) => i !== index);
    setKeywords(newKeywords);
    
    // Auto-save after removing keyword
    try {
      await uploadProtectedKeywords({ protected: newKeywords });
    } catch (error) {
      alert('Failed to save protected keywords');
      // Revert the change if save failed
      setKeywords([...keywords]);
    }
  };



  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeyword();
    }
  };

  if (!currentContext) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Context Selected
          </h3>
          <p className="text-gray-600">
            Please go to the Context Management tab to select a context before managing protected keywords.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add New Protected Keyword */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Add New Protected Keyword</h3>
          <p className="mt-1 text-sm text-gray-500">
            Will be recognized as 1 word and not split into different chunks.
          </p>
          <button
            onClick={() => refreshCurrentContext()}
            className="btn-secondary flex items-center space-x-2"
            title="Refresh context data"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
        
        <div className="flex space-x-3">
          <input
            type="text"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            onKeyPress={handleKeyPress}
            className="input-field flex-1"
            placeholder="Enter protected keyword..."
          />
          
          <button
            onClick={addKeyword}
            disabled={!newKeyword.trim()}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" />
            <span>Add</span>
          </button>
        </div>
      </div>

      {/* Keywords List */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Protected Keywords</h3>
        </div>
        
        {!keywords || keywords.length === 0 ? (
          <div className="text-center py-8">
            <Shield className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No protected keywords defined</p>
            <p className="text-sm text-gray-400">Add keywords above to get started</p>
          </div>
        ) : (
          <div className="space-y-3">
            {keywords.map((keyword, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center space-x-3">
                  <Shield className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-gray-900">{keyword}</span>
                </div>
                
                <button
                  onClick={() => removeKeyword(index)}
                  className="text-red-400 hover:text-red-600 transition-colors duration-200"
                  title="Remove keyword"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Current Context Info */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Current Context</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-700">Context ID</p>
            <p className="text-gray-900">{currentContext.contextId}</p>
          </div>
          
          <div>
            <p className="text-sm font-medium text-gray-700">Current Protected Keywords</p>
                            <p className="text-gray-900">{keywords?.length || 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProtectedKeywordsTab;
